import React, { useState, useEffect, useCallback } from "react";
import {
  Cpu, Zap, Database, Code, Globe, BookOpen, Play, CheckCircle,
  XCircle, RefreshCw, Copy, ChevronDown, ChevronUp, Terminal,
  BarChart3, Layers, Shield, Eye, Settings, Search, Filter
} from "lucide-react";

const API = "/api";

const CATEGORY_COLORS = {
  notes:       { bg: "bg-yellow-500/15", text: "text-yellow-400", border: "border-yellow-500/30" },
  reminders:   { bg: "bg-orange-500/15", text: "text-orange-400", border: "border-orange-500/30" },
  lists:       { bg: "bg-lime-500/15",   text: "text-lime-400",   border: "border-lime-500/30" },
  calendar:    { bg: "bg-blue-500/15",   text: "text-blue-400",   border: "border-blue-500/30" },
  finance:     { bg: "bg-green-500/15",  text: "text-green-400",  border: "border-green-500/30" },
  health:      { bg: "bg-red-500/15",    text: "text-red-400",    border: "border-red-500/30" },
  marketplace: { bg: "bg-purple-500/15", text: "text-purple-400", border: "border-purple-500/30" },
  support:     { bg: "bg-pink-500/15",   text: "text-pink-400",   border: "border-pink-500/30" },
  knowledge:   { bg: "bg-cyan-500/15",   text: "text-cyan-400",   border: "border-cyan-500/30" },
  rapidapi:    { bg: "bg-indigo-500/15", text: "text-indigo-400", border: "border-indigo-500/30" },
  agents:      { bg: "bg-violet-500/15", text: "text-violet-400", border: "border-violet-500/30" },
  feeds:       { bg: "bg-teal-500/15",   text: "text-teal-400",   border: "border-teal-500/30" },
  business:    { bg: "bg-amber-500/15",  text: "text-amber-400",  border: "border-amber-500/30" },
  documents:   { bg: "bg-slate-500/15",  text: "text-slate-400",  border: "border-slate-500/30" },
  platform:    { bg: "bg-rose-500/15",   text: "text-rose-400",   border: "border-rose-500/30" },
};

const cat = (c) => CATEGORY_COLORS[c] || { bg: "bg-gray-500/15", text: "text-gray-400", border: "border-gray-500/30" };

export default function MCPManager() {
  const [activeTab, setActiveTab] = useState("overview");
  const [stats, setStats]         = useState(null);
  const [tools, setTools]         = useState([]);
  const [resources, setResources] = useState([]);
  const [prompts, setPrompts]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [testTool, setTestTool]   = useState(null);
  const [testArgs, setTestArgs]   = useState("{}");
  const [testResult, setTestResult] = useState(null);
  const [testLoading, setTestLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [expandedTool, setExpandedTool] = useState(null);
  const [copied, setCopied]       = useState("");

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, t, r, p] = await Promise.all([
        fetch(`${API}/mcp/stats`).then(x => x.json()),
        fetch(`${API}/mcp/tools`).then(x => x.json()),
        fetch(`${API}/mcp/resources`).then(x => x.json()),
        fetch(`${API}/mcp/prompts`).then(x => x.json()),
      ]);
      setStats(s);
      setTools(t.tools || []);
      setResources(r.resources || []);
      setPrompts(p.prompts || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const runTest = async () => {
    if (!testTool) return;
    setTestLoading(true);
    setTestResult(null);
    try {
      const args = JSON.parse(testArgs || "{}");
      const res = await fetch(`${API}/mcp/execute`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tool: testTool.name, params: args }),
      });
      const data = await res.json();
      setTestResult(data);
    } catch (e) {
      setTestResult({ isError: true, error: e.message });
    } finally {
      setTestLoading(false);
    }
  };

  const copyText = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(""), 1500);
  };

  const mcpEndpoint = `${window.location.origin}/api/mcp`;

  const filteredTools = tools.filter(t => {
    const matchCat = categoryFilter === "all" || t.category === categoryFilter;
    const matchQ   = !searchQuery || t.name.includes(searchQuery) || t.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchCat && matchQ;
  });

  const categories = ["all", ...Array.from(new Set(tools.map(t => t.category)))];

  const tabs = [
    { id: "overview",   label: "Visión General", icon: BarChart3 },
    { id: "tools",      label: `Herramientas (${tools.length})`, icon: Zap },
    { id: "resources",  label: `Recursos (${resources.length})`, icon: Database },
    { id: "prompts",    label: `Prompts (${prompts.length})`, icon: BookOpen },
    { id: "test",       label: "Sandbox", icon: Terminal },
    { id: "integrate",  label: "Integrar", icon: Code },
  ];

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <RefreshCw className="animate-spin text-violet-400 w-8 h-8" />
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-10 h-10 rounded-xl bg-violet-500/20 flex items-center justify-center">
              <Cpu className="w-5 h-5 text-violet-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">OkBot MCP Server</h2>
              <p className="text-gray-400 text-sm">Model Context Protocol — Protocolo estándar para conectar LLMs con OkBot</p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-green-500/15 border border-green-500/30 text-green-400 text-sm font-medium">
            <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
            Operacional
          </span>
          <button onClick={fetchAll} className="btn-secondary text-sm py-2 px-3">
            <RefreshCw size={14} />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 overflow-x-auto no-scrollbar border-b border-white/5 pb-0">
        {tabs.map(t => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                activeTab === t.id
                  ? "border-violet-500 text-violet-400"
                  : "border-transparent text-gray-400 hover:text-white"
              }`}
            >
              <Icon size={14} />
              {t.label}
            </button>
          );
        })}
      </div>

      {/* ── OVERVIEW ─────────────────────────────────────────────────────────── */}
      {activeTab === "overview" && (
        <div className="space-y-6">
          {/* KPI Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              { label: "Herramientas MCP", value: stats?.tools || tools.length, icon: Zap, color: "text-violet-400", bg: "bg-violet-500/10" },
              { label: "Recursos", value: stats?.resources || resources.length, icon: Database, color: "text-blue-400", bg: "bg-blue-500/10" },
              { label: "Prompts", value: stats?.prompts || prompts.length, icon: BookOpen, color: "text-green-400", bg: "bg-green-500/10" },
              { label: "Ejecuciones totales", value: stats?.total_executions ?? "—", icon: BarChart3, color: "text-orange-400", bg: "bg-orange-500/10" },
            ].map(k => (
              <div key={k.label} className="card p-4">
                <div className={`w-9 h-9 rounded-lg ${k.bg} flex items-center justify-center mb-3`}>
                  <k.icon className={`w-5 h-5 ${k.color}`} />
                </div>
                <p className="text-2xl font-bold text-white">{k.value}</p>
                <p className="text-gray-400 text-sm">{k.label}</p>
              </div>
            ))}
          </div>

          {/* Endpoint info */}
          <div className="card p-5 space-y-4">
            <h3 className="text-white font-semibold flex items-center gap-2"><Globe size={16} className="text-cyan-400" /> Endpoint MCP</h3>
            <div className="space-y-2">
              {[
                { label: "HTTP (JSON-RPC 2.0)", url: `${mcpEndpoint}`, method: "POST" },
                { label: "SSE (Streaming)", url: `${mcpEndpoint}/sse`, method: "GET" },
                { label: "Herramientas OpenAI", url: `${mcpEndpoint}/openai-tools`, method: "GET" },
                { label: "Catálogo completo", url: `${mcpEndpoint}/tools`, method: "GET" },
                { label: "Estadísticas", url: `${mcpEndpoint}/stats`, method: "GET" },
              ].map(e => (
                <div key={e.label} className="flex items-center gap-3 bg-white/3 rounded-lg px-3 py-2">
                  <span className={`text-xs font-mono font-bold px-2 py-0.5 rounded ${e.method === "POST" ? "bg-orange-500/20 text-orange-400" : "bg-green-500/20 text-green-400"}`}>
                    {e.method}
                  </span>
                  <span className="text-gray-300 text-sm font-mono flex-1 truncate">{e.url}</span>
                  <button onClick={() => copyText(e.url, e.label)} className="text-gray-500 hover:text-white">
                    {copied === e.label ? <CheckCircle size={14} className="text-green-400" /> : <Copy size={14} />}
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Category breakdown */}
          <div className="card p-5">
            <h3 className="text-white font-semibold mb-4 flex items-center gap-2"><Layers size={16} className="text-violet-400" /> Herramientas por módulo</h3>
            <div className="flex flex-wrap gap-2">
              {categories.filter(c => c !== "all").map(c => {
                const count = tools.filter(t => t.category === c).length;
                const { bg, text, border } = cat(c);
                return (
                  <div key={c} className={`flex items-center gap-2 px-3 py-1.5 rounded-full border ${bg} ${border}`}>
                    <span className={`text-sm font-medium ${text}`}>{c}</span>
                    <span className={`text-xs font-bold ${text}`}>{count}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Top tools */}
          {stats?.top_tools?.length > 0 && (
            <div className="card p-5">
              <h3 className="text-white font-semibold mb-4 flex items-center gap-2"><BarChart3 size={16} className="text-orange-400" /> Herramientas más usadas</h3>
              <div className="space-y-2">
                {stats.top_tools.map((t, i) => (
                  <div key={t._id} className="flex items-center gap-3">
                    <span className="text-gray-500 text-sm w-5 text-right">{i + 1}</span>
                    <span className="text-gray-300 text-sm font-mono flex-1">{t._id}</span>
                    <div className="w-24 h-1.5 bg-white/5 rounded-full overflow-hidden">
                      <div className="h-full bg-violet-500 rounded-full" style={{ width: `${Math.min(100, (t.count / stats.top_tools[0].count) * 100)}%` }} />
                    </div>
                    <span className="text-gray-400 text-sm w-6 text-right">{t.count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── TOOLS ────────────────────────────────────────────────────────────── */}
      {activeTab === "tools" && (
        <div className="space-y-4">
          {/* Filters */}
          <div className="flex flex-wrap gap-3">
            <div className="relative flex-1 min-w-40">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
              <input
                className="input-field pl-9 text-sm w-full"
                placeholder="Buscar herramientas..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
              />
            </div>
            <div className="relative">
              <Filter size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
              <select
                className="input-field pl-9 pr-8 text-sm appearance-none"
                value={categoryFilter}
                onChange={e => setCategoryFilter(e.target.value)}
              >
                {categories.map(c => <option key={c} value={c}>{c === "all" ? "Todas las categorías" : c}</option>)}
              </select>
            </div>
          </div>

          <p className="text-gray-500 text-sm">{filteredTools.length} herramientas</p>

          <div className="space-y-2">
            {filteredTools.map(tool => {
              const { bg, text, border } = cat(tool.category);
              const isOpen = expandedTool === tool.name;
              return (
                <div key={tool.name} className="card overflow-hidden">
                  <button
                    onClick={() => setExpandedTool(isOpen ? null : tool.name)}
                    className="w-full flex items-center gap-3 p-4 text-left hover:bg-white/2 transition-colors"
                  >
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${bg} ${text} ${border} whitespace-nowrap`}>
                      {tool.category}
                    </span>
                    <span className="text-white font-mono text-sm font-medium flex-1">{tool.name}</span>
                    <span className="text-gray-400 text-xs hidden sm:block flex-1 line-clamp-1">{tool.description}</span>
                    <div className="flex items-center gap-2 ml-2">
                      <button
                        onClick={e => { e.stopPropagation(); setTestTool(tool); setTestArgs(JSON.stringify(
                          Object.fromEntries(
                            Object.entries(tool.inputSchema?.properties || {}).map(([k, v]) => [k, v.type === "number" ? 0 : v.type === "array" ? [] : v.type === "boolean" ? false : ""])
                          ), null, 2)); setActiveTab("test"); }}
                        className="text-violet-400 hover:text-violet-300 text-xs px-2 py-1 rounded bg-violet-500/10 border border-violet-500/20"
                      >
                        <Play size={11} />
                      </button>
                      {isOpen ? <ChevronUp size={14} className="text-gray-500" /> : <ChevronDown size={14} className="text-gray-500" />}
                    </div>
                  </button>

                  {isOpen && (
                    <div className="border-t border-white/5 p-4 space-y-3 bg-white/1">
                      <p className="text-gray-300 text-sm">{tool.description}</p>
                      {tool.inputSchema?.properties && (
                        <div>
                          <p className="text-gray-500 text-xs mb-2 font-medium uppercase tracking-wide">Parámetros</p>
                          <div className="space-y-1.5">
                            {Object.entries(tool.inputSchema.properties).map(([key, val]) => {
                              const required = tool.inputSchema?.required?.includes(key);
                              return (
                                <div key={key} className="flex items-start gap-2 text-xs bg-white/3 rounded px-2.5 py-1.5">
                                  <span className="text-white font-mono font-medium w-36 shrink-0">{key}</span>
                                  <span className="text-blue-400 w-16 shrink-0">{val.type}</span>
                                  <span className="text-gray-400 flex-1">{val.description || "—"}</span>
                                  {required && <span className="text-red-400 text-xs shrink-0">requerido</span>}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      <div className="flex gap-2">
                        <button
                          onClick={() => copyText(JSON.stringify(tool, null, 2), `schema_${tool.name}`)}
                          className="btn-secondary text-xs py-1.5 px-3 flex items-center gap-1.5"
                        >
                          {copied === `schema_${tool.name}` ? <CheckCircle size={11} className="text-green-400" /> : <Copy size={11} />}
                          Schema JSON
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── RESOURCES ────────────────────────────────────────────────────────── */}
      {activeTab === "resources" && (
        <div className="space-y-3">
          <p className="text-gray-400 text-sm">Los recursos son fuentes de datos que los modelos pueden leer directamente via la URI.</p>
          {resources.map(r => (
            <div key={r.uri} className="card p-4 flex items-start gap-3">
              <Database className="text-blue-400 w-5 h-5 mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-white font-medium text-sm">{r.name}</p>
                <p className="text-gray-400 text-xs mt-0.5">{r.description}</p>
                <div className="flex items-center gap-2 mt-2">
                  <code className="text-xs text-cyan-400 font-mono bg-black/30 px-2 py-0.5 rounded">{r.uri}</code>
                  <button onClick={() => copyText(r.uri, `res_${r.uri}`)} className="text-gray-500 hover:text-white">
                    {copied === `res_${r.uri}` ? <CheckCircle size={12} className="text-green-400" /> : <Copy size={12} />}
                  </button>
                </div>
              </div>
              <span className="text-xs text-gray-500 font-mono">{r.mimeType}</span>
            </div>
          ))}
        </div>
      )}

      {/* ── PROMPTS ──────────────────────────────────────────────────────────── */}
      {activeTab === "prompts" && (
        <div className="space-y-3">
          <p className="text-gray-400 text-sm">Prompts de sistema pre-construidos para cada caso de uso de OkBot.</p>
          {prompts.map(p => (
            <div key={p.name} className="card p-5">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <p className="text-white font-semibold text-sm font-mono">{p.name}</p>
                  <p className="text-gray-400 text-sm mt-1">{p.description}</p>
                </div>
                <button
                  onClick={() => copyText(p.name, `prompt_${p.name}`)}
                  className="btn-secondary text-xs py-1.5 px-3 flex items-center gap-1.5 shrink-0"
                >
                  {copied === `prompt_${p.name}` ? <CheckCircle size={11} className="text-green-400" /> : <Copy size={11} />}
                  Copiar nombre
                </button>
              </div>
              {p.arguments?.length > 0 && (
                <div className="mt-3 space-y-1">
                  <p className="text-gray-500 text-xs font-medium uppercase tracking-wide">Argumentos</p>
                  {p.arguments.map(a => (
                    <div key={a.name} className="flex items-center gap-2 text-xs">
                      <span className="text-white font-mono">{a.name}</span>
                      {a.required && <span className="text-red-400">requerido</span>}
                      <span className="text-gray-500">— {a.description}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ── SANDBOX / TEST ───────────────────────────────────────────────────── */}
      {activeTab === "test" && (
        <div className="space-y-5">
          <div className="card p-5 space-y-4">
            <h3 className="text-white font-semibold flex items-center gap-2"><Terminal size={16} className="text-violet-400" /> Sandbox de herramientas</h3>

            <div>
              <label className="text-gray-400 text-sm block mb-2">Herramienta</label>
              <select
                className="input-field text-sm w-full"
                value={testTool?.name || ""}
                onChange={e => {
                  const t = tools.find(x => x.name === e.target.value);
                  setTestTool(t || null);
                  if (t) {
                    const defaults = Object.fromEntries(
                      Object.entries(t.inputSchema?.properties || {}).map(([k, v]) => [
                        k,
                        v.type === "number" ? 0 : v.type === "array" ? [] : v.type === "boolean" ? false : ""
                      ])
                    );
                    setTestArgs(JSON.stringify(defaults, null, 2));
                  }
                  setTestResult(null);
                }}
              >
                <option value="">-- Seleccionar herramienta --</option>
                {categories.filter(c => c !== "all").map(c => (
                  <optgroup key={c} label={c.toUpperCase()}>
                    {tools.filter(t => t.category === c).map(t => (
                      <option key={t.name} value={t.name}>{t.name}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>

            {testTool && (
              <div className="p-3 rounded-lg bg-violet-500/10 border border-violet-500/20">
                <p className="text-violet-300 text-sm">{testTool.description}</p>
              </div>
            )}

            <div>
              <label className="text-gray-400 text-sm block mb-2">Argumentos (JSON)</label>
              <textarea
                className="input-field text-sm font-mono w-full resize-none"
                rows={8}
                value={testArgs}
                onChange={e => setTestArgs(e.target.value)}
                placeholder="{}"
              />
            </div>

            <button
              onClick={runTest}
              disabled={!testTool || testLoading}
              className="btn-primary text-sm py-2.5 flex items-center justify-center gap-2 w-full"
            >
              {testLoading ? <RefreshCw size={15} className="animate-spin" /> : <Play size={15} />}
              {testLoading ? "Ejecutando..." : "Ejecutar herramienta"}
            </button>

            {testResult && (
              <div className={`rounded-xl border p-4 ${testResult.isError || testResult.error ? "bg-red-500/10 border-red-500/30" : "bg-green-500/10 border-green-500/30"}`}>
                <div className="flex items-center gap-2 mb-2">
                  {testResult.isError || testResult.error
                    ? <XCircle size={15} className="text-red-400" />
                    : <CheckCircle size={15} className="text-green-400" />}
                  <span className={`text-sm font-medium ${testResult.isError || testResult.error ? "text-red-400" : "text-green-400"}`}>
                    {testResult.isError || testResult.error ? "Error" : "Éxito"}
                  </span>
                  <button
                    onClick={() => copyText(JSON.stringify(testResult, null, 2), "test_result")}
                    className="ml-auto text-gray-500 hover:text-white"
                  >
                    {copied === "test_result" ? <CheckCircle size={12} className="text-green-400" /> : <Copy size={12} />}
                  </button>
                </div>
                <pre className="text-xs text-gray-300 overflow-auto max-h-72 whitespace-pre-wrap">
                  {JSON.stringify(testResult, null, 2)}
                </pre>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── INTEGRATE ────────────────────────────────────────────────────────── */}
      {activeTab === "integrate" && (
        <div className="space-y-5">
          <div className="card p-5 space-y-4">
            <h3 className="text-white font-semibold flex items-center gap-2"><Code size={16} className="text-cyan-400" /> Integración con OpenRouter</h3>
            <p className="text-gray-400 text-sm">Usa las herramientas MCP directamente en llamadas a OpenRouter con tool_calling:</p>
            <div className="relative">
              <pre className="bg-black/40 text-green-300 text-xs rounded-xl p-4 overflow-auto max-h-96 font-mono">
{`import requests

# 1. Obtener herramientas en formato OpenAI
tools_resp = requests.get("${mcpEndpoint}/openai-tools")
tools = tools_resp.json()["tools"]

# 2. Usar en llamada a OpenRouter
response = requests.post(
  "https://openrouter.ai/api/v1/chat/completions",
  headers={
    "Authorization": "Bearer YOUR_OPENROUTER_KEY",
    "Content-Type": "application/json"
  },
  json={
    "model": "anthropic/claude-3.5-sonnet",
    "messages": [{"role": "user", "content": "Crea una nota de mis tareas"}],
    "tools": tools,           # <-- herramientas MCP
    "tool_choice": "auto"
  }
)

# 3. Ejecutar la herramienta que el modelo eligió
tool_call = response.json()["choices"][0]["message"]["tool_calls"][0]
result = requests.post(
  "${mcpEndpoint}/execute",
  json={
    "tool": tool_call["function"]["name"],
    "params": json.loads(tool_call["function"]["arguments"]),
    "context": {"user_id": "USER_ID"}
  }
)`}
              </pre>
              <button
                onClick={() => copyText(`import requests\n\ntools_resp = requests.get("${mcpEndpoint}/openai-tools")\ntools = tools_resp.json()["tools"]`, "python_code")}
                className="absolute top-3 right-3 text-gray-500 hover:text-white"
              >
                {copied === "python_code" ? <CheckCircle size={14} className="text-green-400" /> : <Copy size={14} />}
              </button>
            </div>
          </div>

          <div className="card p-5 space-y-4">
            <h3 className="text-white font-semibold flex items-center gap-2"><Shield size={16} className="text-orange-400" /> Headers de contexto</h3>
            <p className="text-gray-400 text-sm">Pasa el contexto del usuario via headers HTTP:</p>
            <div className="space-y-2">
              {[
                { header: "X-OkBot-User-Id", desc: "ID del usuario activo", example: "usr_abc123" },
                { header: "X-OkBot-Role", desc: "Rol: user, business, admin", example: "user" },
                { header: "X-OkBot-Business-Id", desc: "ID del negocio (modo business)", example: "biz_xyz789" },
                { header: "X-OkBot-Agent-Id", desc: "ID del agente activo", example: "agt_001" },
              ].map(h => (
                <div key={h.header} className="flex items-center gap-3 bg-white/3 rounded-lg px-3 py-2.5 text-sm">
                  <code className="text-cyan-400 font-mono w-48 shrink-0">{h.header}</code>
                  <span className="text-gray-400 flex-1">{h.desc}</span>
                  <code className="text-gray-500 font-mono text-xs">{h.example}</code>
                </div>
              ))}
            </div>
          </div>

          <div className="card p-5 space-y-4">
            <h3 className="text-white font-semibold flex items-center gap-2"><Settings size={16} className="text-violet-400" /> Clientes MCP compatibles</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                { name: "Claude Desktop", status: "compatible", desc: "Configuración SSE nativa" },
                { name: "Cursor IDE", status: "compatible", desc: "MCP tools integration" },
                { name: "Continue.dev", status: "compatible", desc: "Herramientas via JSON-RPC" },
                { name: "LangChain", status: "compatible", desc: "Tool calling con función execute" },
                { name: "OpenRouter", status: "compatible", desc: "Via openai-tools endpoint" },
                { name: "Vercel AI SDK", status: "compatible", desc: "Tool definitions + execute" },
              ].map(c => (
                <div key={c.name} className="flex items-center gap-3 p-3 rounded-lg bg-white/3 border border-white/5">
                  <CheckCircle size={16} className="text-green-400 shrink-0" />
                  <div>
                    <p className="text-white text-sm font-medium">{c.name}</p>
                    <p className="text-gray-500 text-xs">{c.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="card p-5 space-y-3">
            <h3 className="text-white font-semibold flex items-center gap-2"><Eye size={16} className="text-blue-400" /> claude_desktop_config.json</h3>
            <div className="relative">
              <pre className="bg-black/40 text-green-300 text-xs rounded-xl p-4 overflow-auto font-mono">
{`{
  "mcpServers": {
    "okbot": {
      "url": "${mcpEndpoint}/sse",
      "transport": "sse",
      "headers": {
        "X-OkBot-User-Id": "YOUR_USER_ID",
        "X-OkBot-Role": "user"
      }
    }
  }
}`}
              </pre>
              <button
                onClick={() => copyText(`{\n  "mcpServers": {\n    "okbot": {\n      "url": "${mcpEndpoint}/sse",\n      "transport": "sse"\n    }\n  }\n}`, "claude_config")}
                className="absolute top-3 right-3 text-gray-500 hover:text-white"
              >
                {copied === "claude_config" ? <CheckCircle size={14} className="text-green-400" /> : <Copy size={14} />}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
